import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";

import { useSignedIn } from "../../hooks/use-signed-in";
import type { PublicDirectory, SlotPick } from "../../types";

/**
 * The state behind `SlotGateDialog` for any section that shows open times. Call `open` with the
 * time a visitor tapped and spread `gate` onto the dialog.
 *
 * Someone already signed in skips the dialog: the time opens on the coach's booking page in the
 * app with the service and hour picked, one tap from confirmed. Asking them for a name and an
 * email they have already given would read as if they were not signed in at all.
 */
export const useSlotGate = (directory: PublicDirectory | null) => {
  const [pick, setPick] = useState<SlotPick | null>(null);
  const signedIn = useSignedIn();
  const navigate = useNavigate();

  return {
    open: (tapped: SlotPick) => {
      // Every pick carries the business it was made at, for the sign-up on the other side.
      const organizationId = directory?.business.organizationId;
      if (!signedIn) {
        setPick({ ...tapped, organizationId });
        return;
      }
      void navigate({
        to: "/book/$specialistId",
        params: { specialistId: tapped.specialistId },
        search: { serviceId: tapped.serviceId ?? undefined, startAt: tapped.startAt, org: organizationId },
      });
    },
    gate: {
      pick,
      specialist: directory?.specialists.find((specialist) => specialist.id === pick?.specialistId) ?? null,
      serviceName: directory?.services.find((service) => service.id === pick?.serviceId)?.name ?? null,
      onClose: () => setPick(null),
    },
  };
};
